/**
 * Redux store configuration
 * @author Maheen Nasir
 * @description Sets up the Redux store with initial reducer configuration
 */

import { configureStore, combineReducers } from "@reduxjs/toolkit";
import configReducer from "./slices/configSlice";
import userReducer from "./slices/userSlice";
import courseReducer from "./slices/courseSlice";
import notificationReducer from "./slices/notificationSlice";
import modalReducer from "./slices/modalSlice";
import addMembersReducer from "./slices/addMembersSlice";
import courseBuilderReducer from "./slices/courseBuilderSlice";
import tabManagementReducer from "./slices/tabManagementSlice";
import inspectionReducer from "./slices/inspectionSlice";
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from "redux-persist";
import storage from "redux-persist/lib/storage";
import type { ConfigState } from "./slices/configSlice";
import filterModalReducer from "./slices/filterModalSlice";

/**
 * Configured Redux store instance
 * @description Creates and configures the Redux store with initial reducers
 */

const configPersistConfig = {
	key: "config",
	storage,
	whitelist: ["selectedMenuItem"],
};

const rootReducer = combineReducers({
	config: persistReducer<ConfigState>(configPersistConfig, configReducer),
	user: userReducer,
	course: courseReducer,
	notifications: notificationReducer,
	modal: modalReducer,
	filterModal: filterModalReducer,
	addMembers: addMembersReducer,
	courseBuilder: courseBuilderReducer,
	tabManagement: tabManagementReducer,
	inspection: inspectionReducer,
});

export const store = configureStore({
	reducer: rootReducer,
	middleware: getDefaultMiddleware =>
		getDefaultMiddleware({
			serializableCheck: {
				ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
			},
		}),
});

export const persistor = persistStore(store);

/** Type definition for the root state */
export type RootState = ReturnType<typeof store.getState>;
/** Type definition for the dispatch function */
export type AppDispatch = typeof store.dispatch;
